/**
 * BilingualBlock — renders the generated explanation `texte`, which the model produces as a FRENCH
 * section followed by an ARABIC section (the deterministic `critique` urgency pre-template, when
 * present, is the first line of the French section — VAL-3, never re-interpreted here). We split the
 * text at the first Arabic-script line so each half renders in its own `dir` (the French paragraph LTR,
 * the Arabic paragraph RTL) regardless of the shell locale, with `white-space: pre-wrap` to preserve
 * the model's paragraphing. A copy action puts the full original text on the clipboard.
 *
 * No clinical value is recomputed here — the text is the frozen, governed output the server persisted.
 */
import { useMemo } from "react";
import { Button, useToast } from "@/ui";
import { useT } from "@/app";

export interface BilingualBlockProps {
  texte: string;
}

const ARABIC = /[؀-ۿݐ-ݿ]/;

/** Split into the French head + Arabic tail at the first line that contains Arabic script. */
function splitSections(texte: string): { fr: string; ar: string } {
  const lines = texte.split("\n");
  const idx = lines.findIndex((l) => ARABIC.test(l));
  if (idx <= 0) {
    // No clear boundary — show the whole text once, in the direction its own script implies.
    return ARABIC.test(texte) ? { fr: "", ar: texte } : { fr: texte, ar: "" };
  }
  return {
    fr: lines.slice(0, idx).join("\n").trim(),
    ar: lines.slice(idx).join("\n").trim(),
  };
}

export function BilingualBlock({ texte }: BilingualBlockProps) {
  const { t } = useT();
  const toast = useToast();
  const { fr, ar } = useMemo(() => splitSections(texte), [texte]);

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(texte);
      toast.success(t("aix.block.copied"));
    } catch {
      toast.error(t("aix.indispo.error"));
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="ghost" size="sm" leadingIcon="⧉" onClick={copy}>
          {t("aix.block.copy")}
        </Button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: fr && ar ? "1fr 1fr" : "1fr",
          gap: "var(--nv-s-4)",
        }}
      >
        {fr && (
          <section lang="fr" dir="ltr" aria-label={t("aix.block.fr")} style={sectionStyle}>
            <h3 style={headingStyle}>{t("aix.block.fr")}</h3>
            <p style={textStyle}>{fr}</p>
          </section>
        )}
        {ar && (
          <section lang="ar" dir="rtl" aria-label={t("aix.block.ar")} style={sectionStyle}>
            <h3 style={headingStyle}>{t("aix.block.ar")}</h3>
            <p style={textStyle}>{ar}</p>
          </section>
        )}
      </div>
    </div>
  );
}

const sectionStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: "var(--nv-s-2)",
  padding: "var(--nv-s-4)",
  borderRadius: "var(--nv-r-md)",
  background: "var(--nv-surface-2)",
  border: "1px solid var(--nv-line-faint)",
  minWidth: 0,
};

const headingStyle = {
  margin: 0,
  fontSize: "var(--nv-fs-label)",
  color: "var(--nv-slate)",
  fontWeight: 600,
};

const textStyle = {
  margin: 0,
  color: "var(--nv-ink)",
  fontSize: "var(--nv-fs-body)",
  lineHeight: 1.6,
  whiteSpace: "pre-wrap" as const,
};
